import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CheckCircle2, XCircle, Camera, CameraOff, PackageCheck } from 'lucide-react';
import jsQR from 'jsqr';

// Contrôle par scan : chargement ('loading') ou réception ('reception').
const ShipmentScanDialog = ({ open, onOpenChange, shipmentId, checkpoint, onScanned, autoAssign = false }) => {
  const [manual, setManual] = useState('');
  const [feedback, setFeedback] = useState(null); // {type, text}
  const [recon, setRecon] = useState(null);
  const [scanned, setScanned] = useState([]); // OCE codes scannés dans cette session
  const [camOn, setCamOn] = useState(false);
  const [camError, setCamError] = useState('');
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const busyRef = useRef(false);
  const doneRef = useRef(new Set()); // codes déjà traités cette session (anti-boucle caméra)
  const lastRef = useRef({ code: '', at: 0 });

  const isLoading = checkpoint === 'loading';
  const title = isLoading ? 'Contrôle chargement' : 'Contrôle réception';
  // Classes littérales (pas d'interpolation : Tailwind purge les classes dynamiques).
  const chipCls = isLoading ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600';
  const textCls = isLoading ? 'text-blue-600' : 'text-green-600';
  const barCls = isLoading ? 'bg-blue-500' : 'bg-green-500';
  const doneCount = recon ? (isLoading ? recon.loaded_count : recon.received_count) : 0;
  const totalCount = recon?.total || 0;
  const pct = totalCount ? Math.round((doneCount / totalCount) * 100) : 0;
  const missingForStage = isLoading ? (recon?.not_loaded?.length || 0) : (recon?.missing_count || 0);
  const [reporting, setReporting] = useState(false);
  const [summary, setSummary] = useState(null);

  const handleFinish = async () => {
    try {
      const res = await api.get(`/api/shipments/${shipmentId}/loading-summary`);
      setSummary(res.data);
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.detail || 'Erreur' });
    }
  };

  const handleReportMissing = async () => {
    if (!window.confirm(isLoading
      ? `Signaler ${missingForStage} colis non embarqué(s) comme manquants ? (crée des incidents + notifie le collecteur)`
      : `Signaler ${missingForStage} colis non reçu(s) comme manquants ? (crée des incidents + notifie le collecteur)`)) return;
    setReporting(true);
    try {
      const res = await api.post(`/api/shipments/${shipmentId}/report-missing`, { checkpoint });
      setRecon(res.data.reconciliation);
      setFeedback({
        type: res.data.created_incidents > 0 ? 'error' : 'ok',
        text: res.data.created_incidents > 0
          ? `${res.data.created_incidents} incident(s) créé(s) : ${res.data.oce_codes.join(', ')}`
          : 'Aucun colis manquant.',
      });
      onScanned && onScanned();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.detail || 'Erreur' });
    } finally {
      setReporting(false);
    }
  };

  const submitScan = useCallback(async (rawCode) => {
    const code = (rawCode || '').trim();
    if (!code || busyRef.current) return;
    // Code déjà traité cette session → ne pas ré-appeler le backend (anti-boucle).
    if (doneRef.current.has(code)) {
      setFeedback({ type: 'warn', text: `${code} déjà scanné` });
      return;
    }
    // Anti-doublon rapide (même code < 2.5s).
    const now = Date.now();
    if (lastRef.current.code === code && now - lastRef.current.at < 2500) return;
    lastRef.current = { code, at: now };
    busyRef.current = true;
    try {
      const res = await api.post(`/api/shipments/${shipmentId}/scan`, {
        oce_code: code, checkpoint, assign: autoAssign && isLoading,
      });
      const oce = res.data.package.oce_code;
      doneRef.current.add(oce);
      doneRef.current.add(code);
      setRecon(res.data.reconciliation);
      setScanned((prev) => (prev.includes(oce) ? prev : [oce, ...prev]));
      let text;
      if (res.data.already_scanned) text = `${oce} déjà scanné`;
      else if (res.data.assigned) text = `${oce} ✓ assigné + chargé`;
      else text = `${oce} ✓ ${res.data.package.recipient_name || ''}`;
      setFeedback({ type: res.data.already_scanned ? 'warn' : 'ok', text });
      onScanned && onScanned();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.detail || 'Colis introuvable' });
    } finally {
      busyRef.current = false;
    }
  }, [shipmentId, checkpoint, onScanned, autoAssign, isLoading]);

  // Récap initial à l'ouverture.
  useEffect(() => {
    if (!open) return;
    setScanned([]); setFeedback(null); setManual(''); setSummary(null);
    doneRef.current = new Set();
    api.get(`/api/shipments/${shipmentId}/reconciliation`).then(r => setRecon(r.data)).catch(() => {});
  }, [open, shipmentId]);

  // Caméra + détection QR : BarcodeDetector natif si dispo, sinon repli jsQR
  // (nécessaire pour Safari/iOS qui n'a pas BarcodeDetector).
  useEffect(() => {
    if (!open || !camOn) return;
    let raf, timer, detector, canvas, ctx, stopped = false;
    const hasNative = 'BarcodeDetector' in window;

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.setAttribute('playsinline', 'true');
          await videoRef.current.play();
        }
        if (hasNative) {
          detector = new window.BarcodeDetector({ formats: ['qr_code'] });
          const tick = async () => {
            if (stopped || !videoRef.current) return;
            try {
              const codes = await detector.detect(videoRef.current);
              if (codes && codes.length) submitScan(codes[0].rawValue);
            } catch { /* ignore */ }
            raf = requestAnimationFrame(tick);
          };
          raf = requestAnimationFrame(tick);
        } else {
          // Repli jsQR : on décode ~4 images/s depuis un canvas.
          canvas = document.createElement('canvas');
          ctx = canvas.getContext('2d', { willReadFrequently: true });
          timer = setInterval(() => {
            const v = videoRef.current;
            if (stopped || !v || !v.videoWidth) return;
            canvas.width = v.videoWidth;
            canvas.height = v.videoHeight;
            ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
            try {
              const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
              const code = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
              if (code && code.data) submitScan(code.data);
            } catch { /* ignore */ }
          }, 250);
        }
      } catch (e) {
        setCamError("Accès caméra refusé ou indisponible — saisissez le code.");
        setCamOn(false);
      }
    };
    start();
    return () => {
      stopped = true;
      if (raf) cancelAnimationFrame(raf);
      if (timer) clearInterval(timer);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, [open, camOn, submitScan]);

  const fbCls = feedback?.type === 'ok' ? 'bg-green-100 text-green-800'
    : feedback?.type === 'warn' ? 'bg-amber-100 text-amber-800'
    : 'bg-red-100 text-red-800';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setCamOn(false); onOpenChange(o); }}>
      <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className={`inline-flex items-center justify-center h-7 w-7 rounded-md ${chipCls}`}>
              <PackageCheck size={16} />
            </span>
            {title}
          </DialogTitle>
          <DialogDescription>
            {isLoading ? "Scannez chaque colis embarqué avant le départ." : "Scannez chaque colis reçu à l'arrivée — les manquants pourront être signalés."}
          </DialogDescription>
        </DialogHeader>

        {isLoading && autoAssign && (
          <div className="rounded-md bg-blue-50 border border-blue-200 text-blue-800 text-xs px-3 py-2">
            Mode auto : scanner un colis l'<strong>assigne à ce départ</strong> et le <strong>charge</strong> directement.
          </div>
        )}

        {recon && (
          <div className="space-y-3">
            {/* Progression */}
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted-foreground">{isLoading ? 'Chargés' : 'Reçus'}</span>
                <span className="font-semibold">{doneCount}/{totalCount} · {pct}%</span>
              </div>
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                <div className={`h-full rounded-full ${pct >= 100 ? 'bg-green-500' : barCls} transition-all`} style={{ width: `${pct}%` }} />
              </div>
            </div>
            {/* Tuiles */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg border border-border bg-card py-2.5">
                <div className="text-xl font-bold text-foreground">{recon.total}</div>
                <div className="text-[11px] text-muted-foreground">Total</div>
              </div>
              <div className="rounded-lg border border-border bg-card py-2.5">
                <div className={`text-xl font-bold ${textCls}`}>{doneCount}</div>
                <div className="text-[11px] text-muted-foreground">{isLoading ? 'Chargés' : 'Reçus'}</div>
              </div>
              <div className="rounded-lg border border-border bg-card py-2.5">
                <div className={`text-xl font-bold ${recon.missing_count ? 'text-red-600' : 'text-muted-foreground/40'}`}>{recon.missing_count}</div>
                <div className="text-[11px] text-muted-foreground">Manquants</div>
              </div>
            </div>
          </div>
        )}

        {/* Caméra */}
        <div>
          {camOn ? (
            <div className="relative rounded-lg overflow-hidden bg-black aspect-video">
              <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
              <div className="absolute inset-8 border-2 border-white/70 rounded-lg pointer-events-none" />
            </div>
          ) : null}
          <div className="flex gap-2 mt-2">
            <Button type="button" variant="outline" size="sm" onClick={() => { setCamError(''); setCamOn(v => !v); }}>
              {camOn ? <CameraOff className="h-4 w-4 mr-1" /> : <Camera className="h-4 w-4 mr-1" />}
              {camOn ? 'Arrêter la caméra' : 'Scanner avec la caméra'}
            </Button>
          </div>
          {camError && <p className="text-xs text-amber-600 mt-1">{camError}</p>}
        </div>

        {/* Saisie manuelle */}
        <form onSubmit={(e) => { e.preventDefault(); submitScan(manual); setManual(''); }} className="flex gap-2">
          <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Code OCE…" className="font-mono" />
          <Button type="submit"><PackageCheck className="h-4 w-4 mr-1" /> Valider</Button>
        </form>

        {feedback && (
          <div className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm ${fbCls}`}>
            {feedback.type === 'error' ? <XCircle size={16} /> : <CheckCircle2 size={16} />}
            {feedback.text}
          </div>
        )}

        {scanned.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-1">Scannés cette session ({scanned.length})</p>
            <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
              {scanned.map(c => <span key={c} className="text-xs font-mono bg-muted rounded px-1.5 py-0.5">{c}</span>)}
            </div>
          </div>
        )}

        {missingForStage > 0 && (
          <div className="space-y-1">
            {isLoading && !autoAssign && (
              <p className="text-xs text-muted-foreground">
                {missingForStage} colis assigné(s) à ce départ non scanné(s) (absents). Déclarez-les en incident.
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              className="w-full text-red-700 border-red-300 hover:bg-red-50"
              disabled={reporting}
              onClick={handleReportMissing}
            >
              <XCircle className="h-4 w-4 mr-1.5" />
              {reporting ? 'Déclaration…' : `Déclarer ${missingForStage} colis manquant(s) — incident`}
            </Button>
          </div>
        )}

        {/* Récap de fin (collecté vs assigné) — uniquement en mode « Démarrer un chargement ». */}
        {isLoading && autoAssign && !summary && (
          <Button type="button" className="w-full" onClick={handleFinish}>
            <CheckCircle2 className="h-4 w-4 mr-1.5" /> Terminer le scan
          </Button>
        )}

        {summary && (
          <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-green-700">
              <CheckCircle2 size={16} /> {summary.assigned_count} colis affecté(s) à ce départ
            </div>
            <div className="text-sm">
              <p className="text-muted-foreground mb-1">Colis restants (non scannés) : <strong>{summary.remaining_total}</strong></p>
              {summary.remaining_by_partner.length === 0 ? (
                <p className="text-xs text-muted-foreground">Aucun colis restant.</p>
              ) : (
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {summary.remaining_by_partner.map((p) => (
                    <div key={p.partner_id} className="flex items-center justify-between text-xs bg-background rounded px-2 py-1 border border-border">
                      <span className="truncate">{p.partner_name}</span>
                      <span className="font-semibold shrink-0">{p.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button type="button" variant="outline" className="w-full" onClick={() => onOpenChange(false)}>Fermer</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShipmentScanDialog;
